import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProgressRing, getProgressStage } from './progress-ring';
import { 
  BelongingIcon, BalanceIcon, CircleIcon, HandsIcon, 
  DialogueIcon, StewardIcon, TreeIcon, InfinityIcon, CheckIcon,
  IdeaIcon
} from './icons';
import clsx from 'clsx';

// Rebranded section names - official and fancy
const SECTION_META = {
  belonging: {
    title: "The Foundation of Presence",
    description: "Physiological safety & the roots of true connection",
    Icon: BelongingIcon,
    color: 'text-[var(--clay)]',
    subElements: [
      "Welcome rituals & arrival practices",
      "Physical space design for safety",
      "Recognition & visibility protocols",
      "Story-sharing ceremonies",
      "Integration pathways for newcomers"
    ]
  },
  governance: {
    title: "The Council Circle",
    description: "Stewardship structures & collective wisdom",
    Icon: BalanceIcon,
    color: 'text-[var(--primary)]',
    subElements: [
      "Decision-making frameworks",
      "Council roles & responsibilities",
      "Consent vs consensus protocols",
      "Meeting rhythms & sacred times",
      "Documentation & transparency"
    ]
  },
  boundaries: {
    title: "The Sacred Threshold",
    description: "Membership, entry, and honorable departure",
    Icon: CircleIcon,
    color: 'text-[var(--info)]',
    subElements: [
      "Shared values & alignment criteria",
      "Initiation & onboarding journey",
      "Membership agreements & commitments",
      "Graceful exit pathways",
      "Boundary restoration protocols"
    ]
  },
  labor: {
    title: "The Commons of Contribution",
    description: "Shared work, fair exchange & sustainable effort",
    Icon: HandsIcon,
    color: 'text-[var(--accent)]',
    subElements: [
      "Work distribution agreements",
      "Skill-sharing & mentorship",
      "Rest & renewal practices",
      "Recognition of invisible labor",
      "Economic sustainability models"
    ]
  },
  conflict: {
    title: "The Alchemy of Tension",
    description: "Transforming discord into deeper connection",
    Icon: DialogueIcon,
    color: 'text-[var(--warning)]',
    subElements: [
      "Early intervention practices",
      "Restorative dialogue protocols",
      "Elder & witness roles",
      "Accountability without shame",
      "Repair & reconciliation rituals"
    ]
  },
  leadership: {
    title: "The Way of Stewardship",
    description: "Roles, accountability & servant guidance",
    Icon: StewardIcon,
    color: 'text-[var(--earth)]',
    subElements: [
      "Role clarity & boundaries",
      "Selection & rotation practices",
      "Accountability structures",
      "Mentorship & succession",
      "Preventing power concentration"
    ]
  },
  continuity: {
    title: "The Long Arc",
    description: "Legacy, land stewardship & generational vision",
    Icon: TreeIcon,
    color: 'text-[var(--success)]',
    subElements: [
      "Multi-generational planning",
      "Land & resource stewardship",
      "Cultural preservation practices",
      "Story & lore keeping",
      "Adaptation & evolution protocols"
    ]
  },
  interdependence: {
    title: "The Weaving",
    description: "Balance of self & collective, unity in diversity",
    Icon: InfinityIcon,
    color: 'text-[var(--info)]',
    subElements: [
      "Individual sovereignty agreements",
      "Collective care practices",
      "Resource sharing protocols",
      "External relationship guidelines",
      "Network of communities vision"
    ]
  }
};

// Random belonging facts
const BELONGING_FACTS = [
  "Communities without clear governance collapse within 2-3 years on average.",
  "Belonging is physiological - it shows in the face and body before we can name it.",
  "The collapse-cycle repeats: Attraction → Fusion → Disillusionment → Conflict → Burnout → Departure.",
  "Rainbow gatherings taught freedom without structure - festivals, not villages.",
  "A festival dissolves after a week. A village is designed for generations.",
  "Freedom without boundaries becomes chaos. Openness without criteria becomes instability.",
  "If everyone is responsible for everything, no one is responsible for anything.",
  "Communities fail not because people are messy, but because architecture is missing.",
  "Burnout is not personal failure - it's structural inevitability without labor agreements.",
  "Narrative replaces accountability when systems fail: 'They weren't aligned.'",
  "Trust is the ultimate currency of community.",
  "Real community requires: governance, boundaries, pathways, labor, resolution, leadership, continuity, interdependence.",
  "Collapse is not personal. Collapse is structural.",
  "Good intentions create movements. Good architecture creates communities.",
  "People rise to meet solid architecture. They feel safe enough to invest.",
  "The West End worked because it was built on design, not on longing.",
  "Vibes-based communities burn bright and die fast.",
  "Leadership without accountability becomes charisma. Charisma without structure becomes cult.",
  "Healing without regulation becomes trauma bonding.",
  "Consensus without clarity becomes paralysis - endless meetings, no decisions."
];

export const SectionsNav = ({ sections = [], activeSection, onSectionSelect }) => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [currentFact, setCurrentFact] = useState(null);

  const calculateProgress = (section) => {
    if (!section?.content) return 0;
    const wordCount = section.content.trim().split(/\s+/).length;
    return Math.min(100, Math.round((wordCount / 200) * 100));
  };

  const totalProgress = sections.length > 0
    ? Math.round(sections.reduce((acc, s) => acc + calculateProgress(s), 0) / sections.length)
    : 0;

  const showRandomFact = () => {
    const randomIndex = Math.floor(Math.random() * BELONGING_FACTS.length);
    setCurrentFact(BELONGING_FACTS[randomIndex]);
  };

  const toggleExpand = (key) => {
    setExpandedSection(expandedSection === key ? null : key);
  };

  return (
    <div className="bg-[var(--panel)] rounded-[var(--radius-lg)] border border-[var(--muted)]/50 overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-[var(--muted)]/50">
        <h3 className="font-heading text-lg font-semibold text-[var(--fg)]">Charter Architecture</h3>
        <div className="flex items-center gap-3 mt-3">
          <ProgressRing size={48} stroke={5} progress={totalProgress} />
          <div>
            <p className="text-sm font-medium text-[var(--fg)]">
              {totalProgress}% Complete
            </p>
            <p className="text-xs text-[var(--fg)]/60">
              {getProgressStage(totalProgress).stage} Stage {getProgressStage(totalProgress).emoji}
            </p>
          </div>
        </div>
      </div>

      {/* Sections list */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="space-y-1">
          {sections.map((section) => {
            const meta = SECTION_META[section.key] || SECTION_META.belonging;
            const Icon = meta.Icon;
            const progress = calculateProgress(section);
            const { stage, emoji } = getProgressStage(progress);
            const isActive = activeSection === section.key;
            const isExpanded = expandedSection === section.key;

            return (
              <div key={section.key}>
                <motion.button
                  onClick={() => onSectionSelect(section.key)}
                  className={clsx(
                    'w-full flex items-center gap-3 p-3 rounded-[var(--radius-sm)] text-left transition-colors',
                    isActive
                      ? 'bg-[var(--primary)]/10 border border-[var(--primary)]/30'
                      : 'hover:bg-[var(--muted)]/50 border border-transparent'
                  )}
                  whileHover={{ x: 2 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="nav-section-item"
                >
                  <div className="relative">
                    <ProgressRing size={36} stroke={3} progress={progress} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon size={16} className={clsx(meta.color)} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[var(--fg)] truncate">
                        {meta.title}
                      </span>
                      {progress >= 80 && (
                        <CheckIcon size={14} className="text-[var(--success)]" />
                      )}
                    </div>
                    <span className="text-xs text-[var(--fg)]/50">
                      {stage} {emoji}
                    </span>
                  </div>
                  {/* Expand button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(section.key);
                    }}
                    className="p-1 hover:bg-[var(--muted)] rounded transition-colors"
                    title="View elements"
                  >
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      className={clsx('transition-transform', isExpanded && 'rotate-180')}
                    >
                      <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </motion.button>

                {/* Sub-elements */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-12 mr-2 mb-2"
                    >
                      <div className="bg-[var(--sky)]/50 rounded-[var(--radius-xs)] p-3 space-y-2">
                        <p className="text-xs text-[var(--fg)]/70 italic mb-2">{meta.description}</p>
                        {meta.subElements.map((element, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)]/50 mt-1.5 flex-shrink-0" />
                            <span className="text-xs text-[var(--fg)]/80">{element}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>

      {/* Random Fact Section */}
      <div className="p-3 border-t border-[var(--muted)]/50">
        <button
          onClick={showRandomFact}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-[var(--radius-sm)] bg-[var(--accent)]/10 hover:bg-[var(--accent)]/20 text-[var(--accent)] text-xs font-medium transition-colors"
          data-testid="random-fact-button"
        >
          <IdeaIcon size={14} />
          Belonging Wisdom
        </button>
        <AnimatePresence>
          {currentFact && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-2 p-3 bg-[var(--sky)]/50 rounded-[var(--radius-xs)] text-xs text-[var(--fg)]/80 italic"
            >
              "{currentFact}"
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer - The Four Invocations */}
      <div className="p-4 border-t border-[var(--muted)]/50 bg-[var(--sky)]/30">
        <p className="text-xs font-medium text-[var(--fg)]/70 mb-2">The Four Pillars</p>
        <div className="flex flex-wrap gap-1">
          {['Belonging', 'Agency', 'Security', 'Trust'].map((inv) => (
            <span
              key={inv}
              className="text-[10px] px-2 py-0.5 bg-white/60 rounded-full text-[var(--fg)]/60"
            >
              {inv}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
